import { useState, useEffect } from 'react';
import { api } from '../services/api';
import type { TravelTimeResult } from '../types/api';

interface UseTravelTimesReturn {
  travelTimes: TravelTimeResult[];
  loading: boolean;
  error: string | null;
}

/** Module-level cache so results persist across component mounts. */
const travelTimeCache = new Map<string, TravelTimeResult[]>();

/**
 * Hook to fetch locations and compute travel times for a given property.
 *
 * Travel times are not stored on the property record in DynamoDB.
 * The extension computes them on-the-fly using the backend Google Maps
 * endpoint. This hook replicates that behaviour for the web dashboard.
 *
 * Results are cached per property address at module scope so they survive
 * component unmount/remount (e.g. toggling between list and detail views).
 */
export function useTravelTimes(searchId: string | null, propertyAddress: string): UseTravelTimesReturn {
  const [travelTimes, setTravelTimes] = useState<TravelTimeResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!searchId || !propertyAddress) {
      setTravelTimes([]);
      return;
    }

    const cached = travelTimeCache.get(propertyAddress);
    if (cached) {
      setTravelTimes(cached);
      return;
    }

    let cancelled = false;

    // Capture the narrowed non-null searchId so the async closure never
    // needs a non-null assertion.
    const currentSearchId = searchId;

    async function fetchTravelTimes() {
      setLoading(true);
      setError(null);

      try {
        const locations = await api.getLocations(currentSearchId);
        if (cancelled) return;

        if (locations.length === 0) {
          setTravelTimes([]);
          setLoading(false);
          return;
        }

        const destinations = locations.map((loc) => ({
          name: loc.name,
          address: loc.address,
        }));

        const response = await api.calculateTravelTimes(propertyAddress, destinations);
        if (cancelled) return;

        travelTimeCache.set(propertyAddress, response.travelTimes);
        setTravelTimes(response.travelTimes);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load travel times');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchTravelTimes();

    return () => {
      cancelled = true;
    };
  }, [searchId, propertyAddress]);

  return { travelTimes, loading, error };
}

/** Invalidate the cached travel times for a specific address. */
export function invalidateTravelTimeCache(address: string): void {
  travelTimeCache.delete(address);
}

/** @internal Exported only for testing — clears the module-level cache. */
export function _clearTravelTimeCache(): void {
  travelTimeCache.clear();
}
